import { ChatErrorCodes } from '../contracts/chat-types.js';

/**
 * Erro de uma chamada à API: o status HTTP, o `code` do Discord (quando a
 * API manda), os erros por campo (50035), o `retry_after` (429) e o motivo
 * de um 401. Sem rede, `status` é 0.
 */
const UNAVAILABLE_STATUS = new Set([502, 503, 504]);

export class ChatApiError extends Error {
  readonly status: number;
  /** Código do Discord (`ChatErrorCodes`); nulo quando a API não manda. */
  readonly code: number | null;
  /** Erros por campo do 50035 (formato do Discord). */
  readonly errors: unknown;
  /** Segundos até poder tentar de novo (429). */
  readonly retryAfter: number | null;
  /** Motivo de um 401 (`AUTH_REJECTION_REASONS`). */
  readonly reason: string | null;
  /** Login errado: quantas tentativas faltam antes do bloqueio. */
  readonly remainingAttempts: number | null;
  /** Minutos que o bloqueio dura (login). */
  readonly lockMinutes: number | null;
  readonly body: unknown;

  constructor(status: number, body: unknown, fallbackMessage?: string) {
    const data = (body && typeof body === 'object' ? body : {}) as Record<
      string,
      unknown
    >;
    const message =
      typeof data.message === 'string'
        ? data.message
        : (fallbackMessage ?? `Erro ${status} na API do chat.`);
    super(message);
    this.name = 'ChatApiError';
    this.status = status;
    this.code = typeof data.code === 'number' ? data.code : null;
    this.errors = data.errors ?? null;
    // O chat usa `retry_after` (Discord); o bloqueio de login da base, `retryAfterSeconds`.
    this.retryAfter =
      typeof data.retry_after === 'number'
        ? data.retry_after
        : typeof data.retryAfterSeconds === 'number'
          ? data.retryAfterSeconds
          : null;
    this.remainingAttempts =
      typeof data.remainingAttempts === 'number'
        ? data.remainingAttempts
        : null;
    this.lockMinutes =
      typeof data.lockMinutes === 'number' ? data.lockMinutes : null;
    this.reason = typeof data.reason === 'string' ? data.reason : null;
    this.body = body;
  }

  /** A requisição nem chegou (sem rede, servidor fora). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /**
   * Servidor fora do ar: sem rede ou o proxy na frente da API respondendo
   * 502/503/504 (a API caiu ou está reiniciando). Dá para tentar de novo.
   */
  get isUnavailable(): boolean {
    return this.status === 0 || UNAVAILABLE_STATUS.has(this.status);
  }

  get isMissingPermissions(): boolean {
    return this.code === ChatErrorCodes.MISSING_PERMISSIONS;
  }
}

export function isChatApiError(error: unknown): error is ChatApiError {
  return error instanceof ChatApiError;
}
